import { useEffect, useState } from 'react';
import { ChevronLeft, Loader2, CheckCircle2, AlertCircle, ShieldCheck, Ticket, X, ArrowRight } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import {
  useCreateCoachBooking, useCreateBundledBooking, validatePromoCode,
  type PromoValidation,
} from '../../hooks/useAPI';
import supabase from '../../lib/supabase';

interface CoachCheckoutScreenProps {
  booking: {
    coachId: string;
    lessonType: 'private' | 'semi_private' | 'group';
    environment: 'indoor' | 'outdoor';
    sessionMinutes: number;
    scheduledAt: string;
    participants: number;
    sessionCreditId?: string;
    courtId?: string;
    clubId?: string;
  };
  onNavigate: (screen: any, params?: any) => void;
  onConfirmed: () => void;
}

export default function CoachCheckoutScreen({ booking, onNavigate, onConfirmed }: CoachCheckoutScreenProps) {
  const { createCoachBooking, isLoading } = useCreateCoachBooking();
  const { createBundle, isLoading: bundleLoading } = useCreateBundledBooking();
  const isBundled = !!booking.courtId;
  const isCredit  = !!booking.sessionCreditId;
  // Promos apply only to the solo cash coach-booking flow.
  const promoEligible = !isBundled && !isCredit;

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingRef, setBookingRef] = useState<string | null>(null);
  const [courtBookingId, setCourtBookingId] = useState<string | null>(null);
  const [total, setTotal] = useState<{ amount: number; currency: string } | null>(null);
  const [subtotal, setSubtotal] = useState<{ amount: number; currency: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Promo gate. Booking is only created after the user either applies a
  // promo, removes one, or clicks "continue without" — so we don't leave
  // a stale pending_payment row colliding with the coach availability
  // exclusion constraint.
  const [promo, setPromo] = useState<PromoValidation | null>(null);
  const [promoReady, setPromoReady] = useState(!promoEligible); // skip the gate when promo isn't eligible

  // Pre-fetch the coach rate so we can show a subtotal before creating
  // the booking (and so the promo validator has a real amount to work
  // with).
  useEffect(() => {
    if (!promoEligible) return;
    (async () => {
      const { data, error: rateErr } = await supabase
        .from('coach_lesson_types')
        .select('per_person_price, currency')
        .eq('coach_id', booking.coachId)
        .eq('lesson_type', booking.lessonType)
        .eq('environment', booking.environment)
        .eq('session_minutes', booking.sessionMinutes)
        .eq('is_active', true)
        .maybeSingle();
      if (data) {
        setSubtotal({
          amount: Number(data.per_person_price) * booking.participants,
          currency: data.currency,
        });
      } else if (rateErr) {
        // Can't price → skip the promo gate so the booking still
        // proceeds (the edge function will hit the same error and
        // surface a proper message).
        setPromoReady(true);
      }
    })();
  }, [booking.coachId, booking.lessonType, booking.environment, booking.sessionMinutes, booking.participants, promoEligible]);

  useEffect(() => {
    if (!promoReady) return;
    (async () => {
      setError(null);
      if (isBundled) {
        const { data, error } = await createBundle({
          coachId: booking.coachId,
          courtId: booking.courtId!,
          lessonType: booking.lessonType,
          environment: booking.environment,
          sessionMinutes: booking.sessionMinutes,
          scheduledAt: booking.scheduledAt,
          participants: booking.participants,
        });
        if (error) { setError(error.message || 'Could not create bundle'); return; }
        setBookingId(data?.bundle?.coachBooking?.id ?? null);
        setBookingRef(data?.bundle?.coachBooking?.reference ?? null);
        setCourtBookingId(data?.bundle?.courtBooking?.id ?? null);
        setTotal({
          amount: Number(data?.bundle?.grandTotal ?? 0),
          currency: data?.bundle?.currency ?? 'AED',
        });
        setClientSecret(data?.payment?.clientSecret ?? null);
        return;
      }

      const { data, error } = await createCoachBooking({
        ...booking,
        promoCode: promo?.code,
      });
      if (error) { setError(error.message || 'Could not create booking'); return; }
      setBookingId(data?.booking?.id ?? null);
      setBookingRef(data?.booking?.bookingReference ?? null);
      setTotal({
        amount: Number(data?.booking?.totalAmount ?? 0),
        currency: data?.booking?.currency ?? 'AED',
      });
      if (data?.booking?.status === 'confirmed' || data?.booking?.redeemedCredit) {
        setSuccess(true);
        return;
      }
      setClientSecret(data?.payment?.clientSecret ?? null);
    })();
  }, [promoReady]);

  if (success) {
    const start = new Date(booking.scheduledAt);
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-[#D4F04A] mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#1A6B4F]" />
          </div>
          <h2 className="text-2xl mb-2">{isBundled ? 'You\'re on court' : 'Lesson booked'}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {isBundled ? (
              <>
                Lesson <span className="font-medium text-foreground">{bookingRef}</span>
                {' '}and court reserved together.
              </>
            ) : (
              <>Reference <span className="font-medium text-foreground">{bookingRef}</span>.</>
            )}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            {' · '}
            {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
          <button
            onClick={onConfirmed}
            className="h-11 px-6 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            View my bookings
          </button>
        </div>
      </div>
    );
  }

  const start = new Date(booking.scheduledAt);

  // For the promo-eligible path we show subtotal (pre-fetched) and, once
  // a promo is applied, the discount line. For bundled/credit paths we
  // show whatever the edge function returned.
  const displayCurrency = subtotal?.currency || total?.currency || 'AED';
  const displaySubtotal = subtotal?.amount ?? total?.amount ?? null;
  const displayTotal = promo && subtotal
    ? Math.max(0, subtotal.amount - promo.discount)
    : (total?.amount ?? subtotal?.amount ?? null);

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('coach', { id: booking.coachId })}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg">Confirm & pay</h1>
          <p className="text-xs text-muted-foreground">Coaching lesson</p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-5 text-white">
          <div className="text-sm opacity-80 capitalize">
            {booking.lessonType.replace('_', '-')} lesson · {booking.environment}
          </div>
          <div className="text-lg font-medium mt-0.5">
            {booking.sessionMinutes} min · {booking.participants} {booking.participants === 1 ? 'person' : 'people'}
          </div>
          <div className="h-px bg-white/15 my-3" />
          <div className="text-sm">
            {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            {' · '}
            {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {displayTotal !== null && (
          <div className="bg-card border border-border rounded-2xl p-4">
            {promoEligible && displaySubtotal !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Lesson</span>
                <span>{displayCurrency} {displaySubtotal.toFixed(0)}</span>
              </div>
            )}
            {promo && (
              <div className="flex justify-between text-sm mt-1.5 text-[#1A6B4F]">
                <span className="inline-flex items-center gap-1">
                  <Ticket className="w-3.5 h-3.5" /> {promo.code}
                </span>
                <span>-{displayCurrency} {promo.discount.toFixed(0)}</span>
              </div>
            )}
            {promoEligible && (displaySubtotal !== null) && <div className="h-px bg-border my-2" />}
            <div className="flex justify-between text-base">
              <span>Total</span>
              <span className="text-[#1A6B4F] font-medium">
                {displayCurrency} {displayTotal.toFixed(0)}
              </span>
            </div>
          </div>
        )}

        {promoEligible && !clientSecret && subtotal && (
          <PromoCodeInput
            subtotal={subtotal.amount}
            applied={promo}
            onApply={(p) => { setPromo(p); setPromoReady(true); }}
            onRemove={() => { setPromo(null); setPromoReady(true); }}
            onSkip={() => setPromoReady(true)}
            promoReady={promoReady}
          />
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 text-sm text-destructive flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {clientSecret && bookingId ? (
          <Elements stripe={getStripe()} options={{ clientSecret }}>
            <CardPayment
              onSuccess={async () => {
                for (let i = 0; i < 10; i++) {
                  const { data } = await supabase
                    .from('coach_bookings')
                    .select('status')
                    .eq('id', bookingId)
                    .single();
                  if (data?.status === 'confirmed') break;
                  await new Promise((r) => setTimeout(r, 1000));
                }
                // Bundled path also waits briefly for the club transfer
                // to get recorded, just to keep the "Bookings" view tidy.
                if (isBundled && courtBookingId) {
                  for (let i = 0; i < 5; i++) {
                    const { data } = await supabase
                      .from('court_bookings')
                      .select('status')
                      .eq('id', courtBookingId)
                      .single();
                    if (data?.status === 'confirmed') break;
                    await new Promise((r) => setTimeout(r, 1000));
                  }
                }
                setSuccess(true);
              }}
              onError={setError}
            />
          </Elements>
        ) : (isLoading || bundleLoading || promoReady) ? (
          // Spinner only when actually working — see CourtCheckoutScreen
          // for rationale. Hides "Loading payment…" when we're really
          // waiting on the user to skip / apply a promo.
          <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {(isLoading || bundleLoading || !clientSecret)
              ? isBundled ? 'Preparing lesson + court…' : 'Preparing your booking…'
              : 'Loading payment…'}
          </div>
        ) : null}

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
          Secure payment via Stripe. Your coach is paid after the lesson is confirmed.
        </div>
      </div>
    </div>
  );
}

function PromoCodeInput({
  subtotal, applied, onApply, onRemove, onSkip, promoReady,
}: {
  subtotal: number;
  applied: PromoValidation | null;
  onApply: (p: PromoValidation) => void;
  onRemove: () => void;
  onSkip: () => void;
  promoReady: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (applied) {
    return (
      <div className="bg-[#1A6B4F]/5 border border-[#1A6B4F]/20 rounded-2xl p-3 flex items-center justify-between text-sm">
        <div className="inline-flex items-center gap-2 text-[#1A6B4F]">
          <Ticket className="w-4 h-4" />
          <span className="font-mono font-medium">{applied.code}</span>
          <span className="text-xs text-[#1A6B4F]/70">applied</span>
        </div>
        <button
          onClick={onRemove}
          className="w-7 h-7 rounded-full hover:bg-[#1A6B4F]/10 flex items-center justify-center"
          aria-label="Remove promo"
        >
          <X className="w-4 h-4 text-[#1A6B4F]" />
        </button>
      </div>
    );
  }

  if (!open) {
    if (promoReady) {
      return (
        <button
          onClick={() => setOpen(true)}
          className="w-full h-11 rounded-xl border border-border bg-card text-sm inline-flex items-center justify-center gap-2 hover:bg-secondary"
        >
          <Ticket className="w-4 h-4 text-[#1A6B4F]" /> Have a promo code?
        </button>
      );
    }
    return (
      <div className="space-y-2">
        <button
          onClick={onSkip}
          className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          Continue to payment <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => setOpen(true)}
          className="w-full h-10 rounded-xl text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5"
        >
          <Ticket className="w-3.5 h-3.5" /> Have a promo code?
        </button>
      </div>
    );
  }

  const apply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setBusy(true); setErr(null);
    const res = await validatePromoCode(trimmed, subtotal);
    setBusy(false);
    if ('error' in res) {
      setErr(res.error);
      return;
    }
    onApply(res);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-3 space-y-2">
      <label className="text-xs text-muted-foreground">Promo code</label>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
          placeholder="WELCOME25"
          className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm font-mono uppercase tracking-wider"
          autoFocus
        />
        <button
          onClick={apply}
          disabled={busy || !code.trim()}
          className="h-10 px-4 rounded-lg bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Apply
        </button>
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
      <div className="flex justify-between text-xs">
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
          Cancel
        </button>
        <button onClick={onSkip} className="text-muted-foreground hover:text-foreground">
          Continue without code
        </button>
      </div>
    </div>
  );
}

function CardPayment({ onSuccess, onError }: { onSuccess: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements, redirect: 'if_required',
    });
    if (error) {
      onError(error.message || 'Payment failed');
      setBusy(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') onSuccess();
    else { onError('Payment did not succeed'); setBusy(false); }
  };

  return (
    <form onSubmit={pay} className="space-y-3">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || busy}
        className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy ? 'Paying…' : 'Pay now'}
      </button>
    </form>
  );
}
